import { ref } from "vue";

const STORAGE_KEY = "toasthost-demo-mode-enabled";
const MASK_CHAR = "●"; // Black circle - clear visual indicator for masked content

// Global reactive state
const isDemoModeEnabled = ref<boolean>(localStorage.getItem(STORAGE_KEY) === "true");

/**
 * Mask email address for demo mode
 * Example: rory.graves@fieldmark.co.uk -> r●●●●●@●●●●●●●●●.uk
 */
export function maskEmail(email: string | null | undefined): string {
  if (!email) return "";
  if (!isDemoModeEnabled.value) return email;

  const [localPart, domain] = email.split("@");
  if (!domain) return email;

  // Keep first 1 character of local part
  const visibleLocal = localPart.substring(0, Math.min(1, localPart.length));
  const maskedLocal = visibleLocal + MASK_CHAR.repeat(5);

  // Mask domain name but keep TLD
  const domainParts = domain.split(".");
  const tld = domainParts[domainParts.length - 1];
  const maskedDomain = MASK_CHAR.repeat(9);

  return `${maskedLocal}@${maskedDomain}.${tld}`;
}

/**
 * Mask last name for demo mode
 * Example: Graves -> G●●●●●
 */
export function maskLastName(lastName: string | null | undefined): string {
  if (!lastName) return "";
  if (!isDemoModeEnabled.value) return lastName;

  const firstChar = lastName.charAt(0).toUpperCase();
  const maskLength = Math.max(lastName.length - 1, 5);

  return firstChar + MASK_CHAR.repeat(maskLength);
}

/**
 * Mask full name for demo mode
 * Example: Rory Graves -> Rory G●●●●●
 */
export function maskFullName(fullName: string | null | undefined): string {
  if (!fullName) return "";
  if (!isDemoModeEnabled.value) return fullName;

  const parts = fullName.trim().split(/\s+/);

  // Single name - return as is (likely first name only)
  if (parts.length === 1) return parts[0];

  // Multiple parts - keep first name, mask last name
  const firstName = parts[0];
  const lastName = parts[parts.length - 1];
  const maskedLast = maskLastName(lastName);

  // Handle middle names if present
  if (parts.length > 2) {
    const middleNames = parts
      .slice(1, -1)
      .map((name) => name.charAt(0) + MASK_CHAR.repeat(Math.max(name.length - 1, 2)));
    return `${firstName} ${middleNames.join(" ")} ${maskedLast}`;
  }

  return `${firstName} ${maskedLast}`;
}

/**
 * Mask first name for demo mode (usually kept visible for distinguishability)
 * Example: Rory -> Rory (default: keep visible)
 */
export function maskFirstName(firstName: string | null | undefined): string {
  if (!firstName) return "";
  // Keep first names visible for distinguishability
  return firstName;
}

/**
 * Composable for privacy/demo mode
 */
export function usePrivacyMode() {
  function toggleDemoMode() {
    isDemoModeEnabled.value = !isDemoModeEnabled.value;
    localStorage.setItem(STORAGE_KEY, String(isDemoModeEnabled.value));
  }

  function setDemoMode(enabled: boolean) {
    isDemoModeEnabled.value = enabled;
    localStorage.setItem(STORAGE_KEY, String(enabled));
  }

  return {
    isDemoModeEnabled,
    toggleDemoMode,
    setDemoMode,
    // Export masking functions for convenience
    maskEmail,
    maskLastName,
    maskFullName,
    maskFirstName,
  };
}
